import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconControl, Loading, TabPaneControl } from "spark-core-dx/components";
import { useCorePermissions, useGenericManager } from "spark-core-dx/hooks";
import "./BillingManagement.scss";

const DEFAULT_FILTERS = {
  clientId: "",
  fromUtc: "",
  toUtc: "",
};

const SEARCH_SELECT_LIST_FIELD_TYPE = 25;

const BILLING_CLIENT_FILTER_FIELD = {
  DataOperationApp: "CorePortal",
  DataOperationItem: "Client",
  DisplayName: "Client",
  FieldName: "clientId",
  FieldType: SEARCH_SELECT_LIST_FIELD_TYPE,
  PlaceHolder: "All clients",
};

const unwrapResult = (result) => result?.Data ?? result?.data ?? result ?? null;

const getItems = (result) => {
  const normalized = unwrapResult(result);
  if (Array.isArray(normalized?.Items)) {
    return normalized.Items;
  }

  if (Array.isArray(normalized?.items)) {
    return normalized.items;
  }

  if (normalized?.Item) {
    return [normalized.Item];
  }

  if (normalized?.item) {
    return [normalized.item];
  }

  return [];
};

const getDisplayMessage = (result, fallbackMessage) => {
  const normalized = unwrapResult(result);
  return (
    normalized?.DisplayMessage
    ?? normalized?.Message?.DisplayMessage
    ?? normalized?.Message?.Content
    ?? fallbackMessage
  );
};

const toIsoUtc = (value) => {
  if (!value) {
    return undefined;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

const toQueryParams = (filters, includeDiagnostics = true) => {
  const queryParams = {
    includeDiagnostics: includeDiagnostics ? "true" : "false",
  };

  if (filters.clientId?.trim()) {
    queryParams.clientId = filters.clientId.trim();
  }

  const fromUtc = toIsoUtc(filters.fromUtc);
  if (fromUtc) {
    queryParams.fromUtc = fromUtc;
  }

  const toUtc = toIsoUtc(filters.toUtc);
  if (toUtc) {
    queryParams.toUtc = toUtc;
  }

  return queryParams;
};

const formatNumber = (value) => Number(value ?? 0).toLocaleString();

const formatCurrency = (value, currency) => {
  const numericValue = Number(value ?? 0);
  const resolvedCurrency = currency || "USD";

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: resolvedCurrency,
      maximumFractionDigits: 2,
    }).format(numericValue);
  } catch {
    return `${resolvedCurrency} ${numericValue.toFixed(2)}`;
  }
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const Metric = ({ label, value }) => (
  <div className="billing-management-metric">
    <div className="label">{label}</div>
    <div className="value">{value}</div>
  </div>
);

const IconActionButton = ({ iconKey, label, isActive = false, className = "", ...buttonProps }) => (
  <button
    type="button"
    className={`billing-management-icon-button${isActive ? " is-active" : ""}${className ? ` ${className}` : ""}`}
    title={label}
    aria-label={label}
    {...buttonProps}
  >
    <IconControl iconKey={iconKey} width="1.25rem" height="1.25rem" />
  </button>
);

const SummaryTable = ({ title, columns, rows, emptyText }) => (
  <div className="billing-management-table-content">
    <div className="billing-management-section-header">
      <h4>{title}</h4>
    </div>
    <div className="billing-management-table-wrap">
      <table className="billing-management-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="billing-management-empty-cell">{emptyText}</td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={row._id ?? row.BillingIdentityKey ?? `${title}-${index}`}>
                {columns.map((column) => (
                  <td key={column.key}>{column.render ? column.render(row) : row[column.key] ?? ""}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const BillingManagement = () => {
  const { canView } = useCorePermissions();
  const genericMgr = useGenericManager("Generic");
  const genericMgrRef = useRef(genericMgr);
  const appliedFiltersRef = useRef(DEFAULT_FILTERS);
  const hasRequestedClientOptionsRef = useRef(false);
  const refreshRequestIdRef = useRef(0);
  const canViewBilling = canView("BillingManagement");
  const canManageBillingConfiguration = canView("BillingConfigurationAdmin");

  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [summary, setSummary] = useState(null);
  const [documentSummary, setDocumentSummary] = useState([]);
  const [clientSummary, setClientSummary] = useState([]);
  const [usageRows, setUsageRows] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rerating, setRerating] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [lastRefreshDateTime, setLastRefreshDateTime] = useState(null);
  const [activeGridTab, setActiveGridTab] = useState("documentType");

  useEffect(() => {
    genericMgrRef.current = genericMgr;
  }, [genericMgr]);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  const loadOperation = useCallback(async (operationName, queryParams, fallbackMessage) => {
    const manager = genericMgrRef.current;
    if (!manager?.RunOperation) {
      throw new Error("Billing services are unavailable.");
    }

    const result = await manager.RunOperation({
      operationName,
      queryParams,
      isWarning: true,
    });

    const normalized = unwrapResult(result);
    if (!normalized?.Success) {
      throw new Error(getDisplayMessage(normalized, fallbackMessage));
    }

    return normalized;
  }, []);

  const refreshBillingData = useCallback(async (filters) => {
    const effectiveFilters = filters ?? appliedFiltersRef.current;
    const requestId = refreshRequestIdRef.current + 1;
    refreshRequestIdRef.current = requestId;

    setLoading(true);
    setError("");
    setStatusMessage("");

    try {
      const [summaryResult, documentResult, clientResult, usageResult, diagnosticsResult] = await Promise.all([
        loadOperation("LoadBillingSummary", toQueryParams(effectiveFilters, true), "Unable to load billing summary."),
        loadOperation("LoadBillingDocumentTypeSummary", toQueryParams(effectiveFilters, false), "Unable to load billing document totals."),
        loadOperation("LoadBillingClientSummary", toQueryParams(effectiveFilters, false), "Unable to load billing client totals."),
        loadOperation("LoadBillingUsage", toQueryParams(effectiveFilters, false), "Unable to load billing usage."),
        loadOperation("LoadBillingDiagnostics", toQueryParams(effectiveFilters, true), "Unable to load billing diagnostics."),
      ]);

      if (requestId !== refreshRequestIdRef.current) {
        return;
      }

      setSummary(getItems(summaryResult)[0] ?? null);
      setDocumentSummary(getItems(documentResult));
      setClientSummary(getItems(clientResult));
      setUsageRows(getItems(usageResult).slice(0, 50));
      setDiagnostics(getItems(diagnosticsResult).slice(0, 25));
      setLastRefreshDateTime(new Date());
    } catch (loadError) {
      if (requestId !== refreshRequestIdRef.current) {
        return;
      }

      console.error("Billing management load failed:", loadError);
      setError(loadError?.message ?? "Unable to load billing data.");
    } finally {
      if (requestId === refreshRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [loadOperation]);

  useEffect(() => {
    if (!canViewBilling) {
      setLoading(false);
      return;
    }

    void refreshBillingData(appliedFilters);
  }, [appliedFilters, canViewBilling, refreshBillingData]);

  useEffect(() => {
    if (!canViewBilling || !genericMgrRef.current?.GetSelectData || hasRequestedClientOptionsRef.current) {
      return;
    }

    hasRequestedClientOptionsRef.current = true;
    void genericMgrRef.current.GetSelectData(BILLING_CLIENT_FILTER_FIELD);
  }, [canViewBilling, genericMgr]);

  const handleFilterChange = useCallback((key, value) => {
    setDraftFilters((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({
      ...draftFilters,
    });
  }, [draftFilters]);

  const handleResetFilters = useCallback(() => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  }, []);

  const handleRerate = useCallback(async () => {
    setRerating(true);
    setStatusMessage("");
    setError("");

    try {
      const result = await loadOperation(
        "RecalculateBillingUsage",
        toQueryParams(appliedFilters, true),
        "Unable to recalculate billing usage."
      );

      const updatedRows = result?.Value?.UpdatedRows ?? result?.Value?.updatedRows ?? 0;
      setStatusMessage(`Recalculated ${formatNumber(updatedRows)} billing row(s).`);
      await refreshBillingData();
    } catch (rerateError) {
      console.error("Billing rerate failed:", rerateError);
      setError(rerateError?.message ?? "Unable to recalculate billing usage.");
    } finally {
      setRerating(false);
    }
  }, [appliedFilters, loadOperation, refreshBillingData]);

  const summaryMetrics = useMemo(() => ([
    { label: "Transactions", value: formatNumber(summary?.TransactionCount) },
    { label: "Billable Units", value: formatNumber(summary?.BillableUnits) },
    { label: "Sent", value: formatNumber(summary?.SentCount) },
    { label: "Received", value: formatNumber(summary?.ReceivedCount) },
    { label: "Diagnostics", value: formatNumber(summary?.DiagnosticCount) },
    { label: "Rated Amount", value: formatCurrency(summary?.RatedAmount, summary?.Currency) },
  ]), [summary]);

  const clientSelectData = useMemo(
    () => genericMgr?.selectData?.find((selectDataItem) => (
      selectDataItem?.FieldName === BILLING_CLIENT_FILTER_FIELD.FieldName
      || selectDataItem?.DataOperationItem === BILLING_CLIENT_FILTER_FIELD.DataOperationItem
    )),
    [genericMgr?.selectData]
  );

  const clientOptions = useMemo(
    () => (clientSelectData?.jsonData ?? []).map((clientOption) => ({
      text: clientOption?.Text ?? clientOption?.DisplayName ?? clientOption?.text ?? "",
      value: String(clientOption?.Value ?? clientOption?.value ?? clientOption?._id ?? ""),
    })).filter((clientOption) => clientOption.text && clientOption.value),
    [clientSelectData?.jsonData]
  );

  const tableViews = useMemo(() => ([
    {
      key: "documentType",
      label: "By Document Type",
      rows: documentSummary,
      emptyText: "No billable document totals found for the selected scope.",
      columns: [
        { key: "DocumentType", label: "Document Type" },
        { key: "Direction", label: "Direction" },
        { key: "TransactionCount", label: "Transactions", render: (row) => formatNumber(row.TransactionCount) },
        { key: "BillableUnits", label: "Units", render: (row) => formatNumber(row.BillableUnits) },
        { key: "RatedAmount", label: "Amount", render: (row) => formatCurrency(row.RatedAmount, row.Currency) },
      ],
    },
    {
      key: "client",
      label: "By Client",
      rows: clientSummary,
      emptyText: "No client billing totals found for the selected scope.",
      columns: [
        { key: "ClientName", label: "Client" },
        { key: "TransactionCount", label: "Transactions", render: (row) => formatNumber(row.TransactionCount) },
        { key: "BillableUnits", label: "Units", render: (row) => formatNumber(row.BillableUnits) },
        { key: "RatedAmount", label: "Amount", render: (row) => formatCurrency(row.RatedAmount, row.Currency) },
      ],
    },
    {
      key: "usage",
      label: "Recent Usage",
      rows: usageRows,
      emptyText: "No billing usage rows found for the selected scope.",
      columns: [
        { key: "UsageTimestampUtc", label: "Usage Time", render: (row) => formatDateTime(row.UsageTimestampUtc) },
        { key: "Direction", label: "Direction" },
        { key: "DocumentType", label: "Document Type" },
        { key: "TransactionMode", label: "Mode" },
        { key: "BillableUnits", label: "Units", render: (row) => formatNumber(row.BillableUnits) },
        { key: "RatedAmount", label: "Amount", render: (row) => formatCurrency(row.RatedAmount, row.Currency) },
        { key: "DestinationReference", label: "Reference" },
      ],
    },
    {
      key: "diagnostics",
      label: "Diagnostics",
      rows: diagnostics,
      emptyText: "No billing diagnostics found for the selected scope.",
      columns: [
        { key: "UsageTimestampUtc", label: "Usage Time", render: (row) => formatDateTime(row.UsageTimestampUtc) },
        { key: "ClientName", label: "Client" },
        { key: "Direction", label: "Direction" },
        { key: "DocumentType", label: "Document Type" },
        { key: "DiagnosticState", label: "State" },
        { key: "DiagnosticMessage", label: "Message" },
      ],
    },
  ]), [clientSummary, diagnostics, documentSummary, usageRows]);

  const billingReportTabs = useMemo(() => tableViews.map((tableView) => ({
    key: tableView.key,
    title: tableView.label,
    content: (
      <SummaryTable
        title={tableView.label}
        rows={tableView.rows}
        emptyText={tableView.emptyText}
        columns={tableView.columns}
      />
    ),
  })), [tableViews]);

  if (!canViewBilling) {
    return (
      <div className="billing-management-screen">
        <div className="billing-management-empty">
          <IconControl iconKey="payments" />
          <div className="title">Billing is unavailable.</div>
          <div className="description">
            You do not have permission to view this operational screen.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-management-screen">
      <div className="billing-management-header">
        <div>
          <h1>EDI Billing</h1>
          <p>
            Usage, current totals, diagnostics, and document-level reporting for client billing.
          </p>
          <div className="billing-management-meta">
            <span>Last Refresh: {lastRefreshDateTime ? lastRefreshDateTime.toLocaleString() : "Not yet loaded"}</span>
            {summary?.PeriodStartUtc || summary?.PeriodEndUtc ? (
              <span>
                Period: {formatDateTime(summary?.PeriodStartUtc)} to {formatDateTime(summary?.PeriodEndUtc)}
              </span>
            ) : null}
            {canManageBillingConfiguration ? (
              <span>Billing configuration is available under Administration &gt; EDI Billing.</span>
            ) : null}
          </div>
        </div>
        <div className="billing-management-actions" aria-label="Billing actions">
          <IconActionButton
            iconKey="refresh"
            label="Refresh billing data"
            disabled={loading}
            onClick={() => void refreshBillingData()}
          />
          {canManageBillingConfiguration ? (
            <IconActionButton
              className={rerating ? "is-busy" : ""}
              iconKey="sync"
              label={rerating ? "Recalculating usage" : "Recalculate usage"}
              disabled={rerating}
              onClick={() => void handleRerate()}
            />
          ) : null}
        </div>
      </div>

      <section className="billing-management-filters">
        <label className="billing-management-client-filter">
          <span>Client</span>
          <select
            value={draftFilters.clientId}
            onChange={(event) => handleFilterChange("clientId", event.target.value)}
          >
            <option value="">All clients</option>
            {clientOptions.map((clientOption) => (
              <option key={clientOption.value} value={clientOption.value}>
                {clientOption.text}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>From</span>
          <input
            type="datetime-local"
            value={draftFilters.fromUtc}
            onChange={(event) => handleFilterChange("fromUtc", event.target.value)}
          />
        </label>
        <label>
          <span>To</span>
          <input
            type="datetime-local"
            value={draftFilters.toUtc}
            onChange={(event) => handleFilterChange("toUtc", event.target.value)}
          />
        </label>
        <div className="billing-management-filter-actions">
          <IconActionButton iconKey="filterlist" label="Apply filters" onClick={handleApplyFilters} />
          <IconActionButton iconKey="undo" label="Reset filters" onClick={handleResetFilters} />
        </div>
      </section>

      {error ? <div className="billing-management-alert error">{error}</div> : null}
      {statusMessage ? <div className="billing-management-alert success">{statusMessage}</div> : null}

      {loading ? (
        <div className="billing-management-loading">
          <Loading />
        </div>
      ) : (
        <>
          <section className="billing-management-summary">
            {summaryMetrics.map((metric) => (
              <Metric key={metric.label} label={metric.label} value={metric.value} />
            ))}
          </section>

          <section className="billing-management-table-stack">
            <section className="billing-management-section billing-management-report-section">
              <TabPaneControl
                className="billing-management-report-tabs"
                tabs={billingReportTabs}
                activeKey={activeGridTab}
                onChange={(_, __, nextKey) => setActiveGridTab(nextKey)}
                align="right"
                compact
                framed={false}
                idPrefix="billing-management-report-tabs"
              />
            </section>
          </section>
        </>
      )}
    </div>
  );
};

export default BillingManagement;
